import { useState } from 'react';
import APIUtils from '../../utils/APIUtils';

// Encapsulates all General-tab "restricted conditions" state and handlers,
// including the cutoff-conflict warning dialog flow that is only triggered
// from this feature. Cutoff value setters are injected because enabling/
// disabling a condition may also flip the `enabled` flag on manual cutoffs.
const useRestrictedConditions = ({ programId, setCutoffValues, setInitialCutoffValues, showSnackbar }) => {
  const [disabledConditions, setDisabledConditions] = useState([]);
  const [enabledConditions, setEnabledConditions] = useState([]);
  const [initialDisabledConditions, setInitialDisabledConditions] = useState([]);
  const [initialEnabledConditions, setInitialEnabledConditions] = useState([]);
  const [selectedDisabled, setSelectedDisabled] = useState([]);
  const [selectedEnabled, setSelectedEnabled] = useState([]);
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [conditionsChecking, setConditionsChecking] = useState(false);
  const [conditionsDirty, setConditionsDirty] = useState(false);
  const [conditionsError, setConditionsError] = useState('');
  const [cutoffWarningDialogOpen, setCutoffWarningDialogOpen] = useState(false);
  const [cutoffWarningConditions, setCutoffWarningConditions] = useState([]);

  const handleToggleDisabledSelection = (condition) => {
    setSelectedDisabled(prev =>
      prev.includes(condition) ? prev.filter(c => c !== condition) : [...prev, condition]
    );
  };

  const handleToggleEnabledSelection = (condition) => {
    setSelectedEnabled(prev =>
      prev.includes(condition) ? prev.filter(c => c !== condition) : [...prev, condition]
    );
  };

  const handleMoveToEnabled = () => {
    if (selectedDisabled.length === 0) return;
    setEnabledConditions(prev => [...prev, ...selectedDisabled].sort());
    setDisabledConditions(prev => prev.filter(c => !selectedDisabled.includes(c)));
    setSelectedDisabled([]);
    setConditionsDirty(true);
  };

  const handleMoveToDisabled = () => {
    if (selectedEnabled.length === 0) return;
    setDisabledConditions(prev => [...prev, ...selectedEnabled].sort());
    setEnabledConditions(prev => prev.filter(c => !selectedEnabled.includes(c)));
    setSelectedEnabled([]);
    setConditionsDirty(true);
  };

  const handleCancelConditions = () => {
    setDisabledConditions([...initialDisabledConditions].sort());
    setEnabledConditions([...initialEnabledConditions].sort());
    setSelectedDisabled([]);
    setSelectedEnabled([]);
    setConditionsDirty(false);
    setConditionsError('');
  };

  const handleApplyConditions = async () => {
    setConditionsChecking(true);  // Apply button spinner only — no list-box spinners yet
    setConditionsError('');
    try {
      // Determine which conditions are being newly disabled or enabled
      const newlyDisabled = disabledConditions.filter(c => !initialDisabledConditions.includes(c));
      const newlyEnabled = enabledConditions.filter(c => !initialEnabledConditions.includes(c));

      if (newlyDisabled.length > 0 || newlyEnabled.length > 0) {
        // Fetch fresh cutoff values to check for conflicts
        const cutoffResponse = await APIUtils.getCutoffValues(programId);
        const manualCutoffs = Array.isArray(cutoffResponse?.manualCutOff) ? cutoffResponse.manualCutOff : [];

        // Check if any newly-disabled conditions have enabled (not explicitly false) in manual cutoffs
        const conflictingConditions = manualCutoffs.filter(c => {
          const acronym = c.acronym || c.Acronym;
          return acronym && newlyDisabled.includes(acronym) && c.enabled !== false;
        });

        if (conflictingConditions.length > 0) {
          // Conflict found — stop the Apply button spinner and show the warning dialog
          setConditionsChecking(false);
          setCutoffWarningConditions(conflictingConditions);
          setCutoffWarningDialogOpen(true);
          return;
        }

        // No disable conflicts — silently update enabled flags for newly-enabled conditions
        const conditionsToEnable = manualCutoffs.filter(c => {
          const acronym = c.acronym || c.Acronym;
          return acronym && newlyEnabled.includes(acronym) && c.enabled === false;
        });

        if (conditionsToEnable.length > 0) {
          const updatedCutoffs = manualCutoffs.map(c => {
            const acronym = c.acronym || c.Acronym;
            if (acronym && newlyEnabled.includes(acronym) && c.enabled === false) {
              return { ...c, enabled: true };
            }
            return c;
          });
          await APIUtils.updateCutoffValues(programId, updatedCutoffs);
          setCutoffValues(updatedCutoffs);
          setInitialCutoffValues(JSON.parse(JSON.stringify(updatedCutoffs)));
        }
      }

      // Conflict check passed (or skipped) — transition to full loading (Apply + list boxes)
      setConditionsChecking(false);
      setConditionsLoading(true);

      // Proceed with updating restricted conditions
      const result = await APIUtils.updateRestrictedConditions(programId, disabledConditions);
      const msg = typeof result === 'string' ? result : (result?.message || 'Restricted conditions updated successfully.');
      showSnackbar(msg);
      setInitialDisabledConditions([...disabledConditions]);
      setInitialEnabledConditions([...enabledConditions]);
      setConditionsDirty(false);
    } catch (err) {
      console.error('Error updating restricted conditions:', err);
      const errMsg = err.response?.data?.message || err.response?.data || 'An error occurred while updating restricted conditions.';
      setConditionsError(typeof errMsg === 'string' ? errMsg : 'An error occurred while updating restricted conditions.');
    } finally {
      setConditionsChecking(false);
      setConditionsLoading(false);
    }
  };

  const handleConfirmCutoffWarning = async () => {
    setCutoffWarningDialogOpen(false);
    setConditionsLoading(true);
    setConditionsError('');
    try {
      // Fetch fresh cutoff values
      const cutoffResponse = await APIUtils.getCutoffValues(programId);
      const manualCutoffs = Array.isArray(cutoffResponse?.manualCutOff) ? cutoffResponse.manualCutOff : [];

      const newlyDisabled = disabledConditions.filter(c => !initialDisabledConditions.includes(c));
      const newlyEnabled = enabledConditions.filter(c => !initialEnabledConditions.includes(c));

      // Update enabled flags: false for newly disabled, true for newly enabled
      const updatedCutoffs = manualCutoffs.map(c => {
        const acronym = c.acronym || c.Acronym;
        if (acronym && newlyDisabled.includes(acronym)) {
          return { ...c, enabled: false };
        }
        if (acronym && newlyEnabled.includes(acronym) && c.enabled === false) {
          return { ...c, enabled: true };
        }
        return c;
      });

      // 1st: Update cutoff values
      await APIUtils.updateCutoffValues(programId, updatedCutoffs);
      setCutoffValues(updatedCutoffs);
      setInitialCutoffValues(JSON.parse(JSON.stringify(updatedCutoffs)));

      // 2nd: Update restricted conditions
      const result = await APIUtils.updateRestrictedConditions(programId, disabledConditions);
      const msg = typeof result === 'string' ? result : (result?.message || 'Restricted conditions updated successfully.');
      showSnackbar(msg);
      setInitialDisabledConditions([...disabledConditions]);
      setInitialEnabledConditions([...enabledConditions]);
      setConditionsDirty(false);
    } catch (err) {
      console.error('Error updating conditions:', err);
      const errMsg = err.response?.data?.message || err.response?.data || 'An error occurred while updating conditions.';
      setConditionsError(typeof errMsg === 'string' ? errMsg : 'An error occurred while updating conditions.');
    } finally {
      setConditionsLoading(false);
      setCutoffWarningConditions([]);
    }
  };

  const handleCancelCutoffWarning = () => {
    setCutoffWarningDialogOpen(false);
    setCutoffWarningConditions([]);
  };

  return {
    // State
    disabledConditions,
    enabledConditions,
    initialDisabledConditions,
    initialEnabledConditions,
    selectedDisabled,
    selectedEnabled,
    conditionsLoading,
    conditionsChecking,
    conditionsDirty,
    conditionsError,
    cutoffWarningDialogOpen,
    cutoffWarningConditions,

    // Setters consumed by data-loading and other flows
    setDisabledConditions,
    setEnabledConditions,
    setInitialDisabledConditions,
    setInitialEnabledConditions,
    setConditionsDirty,
    setConditionsError,
    setConditionsLoading,

    // Handlers
    handleToggleDisabledSelection,
    handleToggleEnabledSelection,
    handleMoveToEnabled,
    handleMoveToDisabled,
    handleCancelConditions,
    handleApplyConditions,
    handleConfirmCutoffWarning,
    handleCancelCutoffWarning,
  };
};

export default useRestrictedConditions;
